import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule } from '@jsverse/transloco';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { TrixelComponent } from '@rma/generic/ui/trixels';
import { DeviceSizeService, RmaBreakpoint } from '@rma/generic/util/device-size';
import { ShortTextPipe } from '@rma/generic/util/pipes/short-text';
import { GoogleAdConversionTrackerService } from '@rma/generic/util/tracking/google-ad-conversion';
import { MetaPixelService } from '@rma/generic/util/tracking/meta-pixel';
import { Angulartics2Module } from 'angulartics2';
import { Observable, Subject, combineLatest, map, startWith } from 'rxjs';
export type ButtonType = 'outlined' | 'flat' | 'link';

@Component({
  selector: 'rma-agent-phone-link',
  imports: [
    Angulartics2Module,
    AsyncPipe,
    MatButtonModule,
    RmaIconComponent,
    NgTemplateOutlet,
    ShortTextPipe,
    TranslocoModule,
    TrixelComponent,
  ],
  templateUrl: './agent-phone-link.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { ngSkipHydration: 'true' },
})
export class AgentPhoneLinkComponent {
  private readonly googleAdConversionTrackerService = inject(GoogleAdConversionTrackerService);
  private readonly metaPixelService = inject(MetaPixelService);

  @Input()
  public phone = '';

  @Input()
  public trackingPhone?: string = undefined;

  @Input()
  public agentCode = '';

  @Input()
  customLabel = '';

  @Input()
  public revealLabel = '';

  @Input()
  public revealAction = '';

  @Input()
  public callMadeLabel = '';

  @Input()
  public callMadeAction = '';

  @Input()
  public trixelUrl?: string | null;

  @Input()
  public trixelRevealCategory?: string;

  @Input()
  public trixelCallCategory?: string;

  @Input()
  public trixelReferringCategory?: string;

  @Input()
  public buttonType: ButtonType = 'outlined';

  @Input()
  public showIcon = true;

  @Input()
  public useMetaPixel = false;

  public isSmallDevice$: Observable<boolean>;
  public showFullNumber$: Observable<boolean>;
  public showCallTrixel$: Observable<boolean>;

  private numberCalled = new Subject<boolean>();
  private numberRevealed = new Subject<boolean>();

  public constructor() {
    const deviceSizeService = inject(DeviceSizeService);

    const display$ = this.numberRevealed.pipe(startWith(false));

    this.showCallTrixel$ = this.numberCalled.asObservable().pipe(startWith(false));

    this.isSmallDevice$ = deviceSizeService.observe(RmaBreakpoint['<LG']).pipe(startWith(true));

    this.showFullNumber$ = combineLatest([display$, this.isSmallDevice$]).pipe(
      map(([display, isSmall]) => display || isSmall || !!this.trackingPhone),
    );
  }

  public onReveal(): void {
    this.numberRevealed.next(true);
    this.googleAdConversionTrackerService.trackPhoneRevealAdConversion();
  }

  public onCall(): void {
    this.numberCalled.next(true);
    this.googleAdConversionTrackerService.trackCallClickAdConversion();
    if (this.useMetaPixel) {
      this.metaPixelService.track('Contact');
    }
  }
}
